import {
  HighwaysCarriagewaysFeatureAttributes,
  ServiceLocationDetailsObjectType,
} from '@types';
import { addressResponseType } from '@utils';

export const convertAddressToServiceLocation = (
  address: addressResponseType,
): ServiceLocationDetailsObjectType => {
  const {
    address_line_1,
    address_line_2,
    town,
    postcode,
    country,
    UPRN,
    USRN,
    latitude,
    longitude,
    county,
    full_address,
    easting,
    northing,
  } = address;

  return {
    address_line_1,
    address_line_2,
    town_city: town,
    postcode,
    country,
    UPRN,
    USRN,
    latitude: latitude ? parseFloat(latitude) : null,
    longitude: longitude ? parseFloat(longitude) : null,
    county,
    easting: easting ?? '',
    northing: northing ?? '',
    full_address,
  };
};

/**
 * Refer to the ServiceLocationDetailsType in the API documentation: https://wcc-dev-apim-external.developer.azure-api.net/api-details#api=wcc-highways-reports-api&operation=create&definition=ServiceLocationDetailsType
 * @param serviceLocation
 * @param serviceAssetDetails
 * @returns addressResponseType
 */
export const convertServiceLocationToAddress = (
  serviceLocation: ServiceLocationDetailsObjectType,
  serviceAssetDetails?: HighwaysCarriagewaysFeatureAttributes,
): addressResponseType => {
  const {
    address_line_1,
    address_line_2,
    town_city,
    postcode,
    country,
    UPRN,
    USRN,
    latitude,
    longitude,
    county,
    full_address,
  } = serviceLocation;

  const fullAddress =
    full_address ||
    (address_line_1 && address_line_2 && postcode
      ? `${address_line_1}, ${address_line_2}, ${postcode}`
      : '');

  return {
    address_line_1,
    address_line_2,
    town: town_city,
    postcode,
    country,
    UPRN,
    USRN,
    latitude: latitude ? latitude.toString() : '',
    longitude: longitude ? longitude.toString() : '',
    county,
    full_address: formatFullAddress(fullAddress),
    asset: serviceAssetDetails,
  };
};

const removeExtraWhitespace = (address: string) => {
  // Remove leading, trailing, and extra internal whitespace
  return address.trim().replace(/\s+/g, ' ');
};

export const capitalizeWord = (word: string) => {
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};

/**
 * Remove leading comma and any extra commas
 * @param address
 * @returns formatted address
 */
export const removeExtraComma = (address: string) => {
  return address.replace(/^,\s*/, '').replace(/,\s*,+/g, ', ');
};

const smallWords = [
  'and',
  'or',
  'the',
  'a',
  'an',
  'in',
  'on',
  'at',
  'to',
  'with',
  'for',
  'by',
  'of',
];

const titleCaseAddress = (address: string) => {
  return address
    .split(' ')
    .map((word, index) => {
      // Always capitalize the first word
      if (index === 0) {
        return capitalizeWord(word);
      }

      // Capitalize if not a small word
      if (!smallWords.includes(word.toLowerCase())) {
        return capitalizeWord(word);
      }

      // Return the small word in lowercase
      return word.toLowerCase();
    })
    .join(' ');
};

export const formatFullAddress = (fullAddress: string) => {
  const fullAddressExPostcode = removeExtraWhitespace(fullAddress)
    .split(' ')
    .slice(0, -2)
    .join(' ');
  const postcode = fullAddress.split(' ').slice(-2).join(' ');
  return removeExtraComma(
    titleCaseAddress(fullAddressExPostcode) + ', ' + postcode.toUpperCase(),
  );
};
